import React, { useEffect, useState } from "react";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import ContactField from "../Contacts/ContactField";
import {
  useDeploySolutionMutation,
  useGetImportedSolutionInfoByUuidQuery,
  useGetSolutionForDeploymentByUuidQuery,
  useResetImportedSolutionInfoByUuidMutation,
  useSetImportedSolutionVariableDefaultsMutation,
  useUpdateApiConnectionMutation,
} from "../store/slices/settingsSlice";
import { Form, InputGroup, Modal, Tab, Tabs } from "react-bootstrap";
import { makeid, shortUUID } from "../helpers";
import {
  useCreateContactSetMutation,
  useGetContactSetsQuery,
  useLazyFindMatchingContactFieldsQuery,
  useUpsertFieldMutation,
} from "../store/slices/contactsSlice";
import {
  useCreateReportingTemplateMutation,
  useCreateReportingTopicMutation,
  useGetMatchingReportingTemplatesQuery,
  useGetReportingTopicsQuery,
  useLazyGetMatchingReportingTemplatesQuery,
} from "../store/slices/reportingSlice";
import {
  isEmptyOrUnmapped,
  hasEmptyNewFieldName,
  FIELD_TYPE_MAPPINGS,
} from "./utils/solutionImportHelpers";
import { handleCheckMatchingFields } from "./services/fieldMatchingService";
import { handleCreateFields } from "./services/fieldCreationService";
import { prepareSolutionDeployData } from "./services/deploymentService";
import { parseSolutionData } from "./services/jsonParsingService";
import SolutionSetup from "../SolutionsBuilder/SolutionSetup";
import SolutionImportInput from "./SolutionImportInput";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
// TODO: When the user wants to manage a field manually and wants to select an existing field from the system, we need to check if a field with that name already exists and map it accordingly.
export default function SolutionImportModal({
  solutionClientUuid,
  setShow,
}: {
  solutionClientUuid: string;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  // const solutionDataRes = useGetSolutionForDeploymentByUuidQuery(
  //   {
  //     uuid: solutionClientUuid,
  //   },
  //   { skip: !solutionClientUuid }
  // );
  // Variable defaults
  const infoResponse = useGetImportedSolutionInfoByUuidQuery(
    { uuid: solutionClientUuid },
    { skip: !solutionClientUuid }
  );
  const solutionInfo = infoResponse?.data || {};
  const solutionData = solutionInfo?.LatestSource?.Solution;
  const [mapping, setMapping] = useState<any>({});
  const [importedJSON, setImportedJSON] = useState<any>({
    Bag: { Internal: {}, External: {} },
    Item: {},
  });

  const [deploy] = useDeploySolutionMutation();
  const [upsertField] = useUpsertFieldMutation();
  const [createReportingTemplate] = useCreateReportingTemplateMutation();
  const [createReportingTopic] = useCreateReportingTopicMutation();
  const [createContactSet] = useCreateContactSetMutation();
  const [updateApiConnection] = useUpdateApiConnectionMutation();
  const [save] = useSetImportedSolutionVariableDefaultsMutation();
  const [reset] = useResetImportedSolutionInfoByUuidMutation();
  const [monitors, setMonitors] = useState<any>({});
  const [inboundAPIs, setInboundAPIs] = useState<any>({});
  const [inboundCampaigns, setInboundCampaigns] = useState<any>({});
  const [apiProfiles, setApiProfiles] = useState<any>({});
  const [findMatchingContactFields] = useLazyFindMatchingContactFieldsQuery();
  const [findMatchingTemplates] = useLazyGetMatchingReportingTemplatesQuery();
  const [manualFields, setManualFields] = useState<string[]>([]);
  const [automaticallySelectedFields, setAutomaticallySelectedFields] =
    useState<string[]>([]);
  const [isDeployDisabled, setIsDeployDisabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const confirmDialog = useConfirmDialog() as
    | null
    | ((title: string, message: string) => Promise<boolean>);

  const { data: reportingTopicsData } = useGetReportingTopicsQuery({
    pagination: { page: 0, perPage: 100 },
  });
  const { data: contactSetsData } = useGetContactSetsQuery({
    page: 0,
    perPage: 100,
  });

  useEffect(() => {
    if (!solutionData?.Components || !contactSetsData || !reportingTopicsData)
      return;
    (async () => {
      // Parse solution data and build mapping
      const { mapping: m, importedJSON: newImportedJSON } =
        parseSolutionData(solutionData);
      setMapping(m);

      // Check for matching fields in the system
      const newExternal = await handleCheckMatchingFields(
        newImportedJSON,
        {
          findMatchingContactFields,
          findMatchingTemplates,
          contactSetsData,
          reportingTopicsData,
        },
        setDeployProgress,
        setAutomaticallySelectedFields
      );

      setImportedJSON({
        ...newImportedJSON,
        Bag: {
          ...newImportedJSON.Bag,
          External: { ...newImportedJSON.Bag.External, ...newExternal },
        },
      });

      setIsDeployDisabled(false);
    })();
  }, [solutionData, contactSetsData, reportingTopicsData]);

  const [showDeployModal, setShowDeployModal] = useState(false);
  const [defaultBaseUrl, setDefaultBaseUrl] = useState("");
  const [variableDefaults, setVariableDefaults] = useState({});

  const [isDeploying, setIsDeploying] = useState(false);
  const [deployProgress, setDeployProgress] = useState("");
  const inboundMessageComponentUuids = (solutionData?.Components || [])
    .filter((component: any) => component.Role === "inbound-message")
    .map((component: any) => component.ComponentUuid)
    .filter(Boolean);
  const hasMissingInboundChannel = inboundMessageComponentUuids.some(
    (componentUuid: string) => !inboundCampaigns?.[componentUuid]?.Inbound
  );

  const handleCreateFieldsWrapper = async () => {
    const updates = await handleCreateFields(
      importedJSON,
      manualFields,
      {
        upsertField,
        createContactSet,
        createReportingTemplate,
        createReportingTopic,
        updateApiConnection,
      },
      defaultBaseUrl,
      setDeployProgress
    );

    const updatedImportedJSON = {
      ...importedJSON,
      Bag: {
        ...importedJSON.Bag,
        External: { ...importedJSON.Bag.External },
      },
    };

    Object.entries(updates || {}).forEach(([fieldType, fieldUpdates]) => {
      Object.entries(fieldUpdates || {}).forEach(([key, value]) => {
        if (updatedImportedJSON.Bag.External[fieldType]) {
          updatedImportedJSON.Bag.External[fieldType][key] = value;
        }
      });
    });

    setImportedJSON(updatedImportedJSON);

    return updatedImportedJSON;
  };

  const handleDeploy = async () => {
    setIsDeploying(true);
    setDeployProgress("Preparing deployment...");

    console.log("Manual fields:", manualFields);

    try {
      // Create any unmapped fields (excluding manually managed fields) before deploying
      const updatedImportedJSON = await handleCreateFieldsWrapper();

      setDeployProgress("Deploying solution...");

      // Prepare deployment data using the service
      const solutionDeployData = prepareSolutionDeployData(
        solutionData,
        solutionClientUuid,
        variableDefaults,
        mapping,
        updatedImportedJSON,
        monitors,
        inboundAPIs,
        inboundCampaigns
      );

      await deploy(solutionDeployData);
      setDeployProgress("Deployment completed successfully!");

      // Reset after a brief delay to show success message
      setTimeout(() => {
        setIsDeploying(false);
        setDeployProgress("");
        setShowDeployModal(false);
        setShow(false);
      }, 2000);
    } catch (error) {
      setDeployProgress("Deployment failed. Please try again.");
      console.log(error);
      setTimeout(() => {
        setIsDeploying(false);
        setDeployProgress("");
      }, 3000);
    }
  };

  return (
    <div className="position-relative">
      {deployProgress && (
        <div className="position-absolute top-0 start-0 h-100 w-100 bg-white p-3 bg-opacity-50 d-flex justify-content-center align-items-center">
          <div className="alert alert-info mt-3 d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span>{deployProgress}</span>
          </div>
        </div>
      )}
      {importedJSON.Bag.External && (
        <div className="p-3">
          <SolutionSetup
            key={
              solutionInfo?.SolutionClientSettings?.VariableDefaults
                ? JSON.stringify(
                    solutionInfo.SolutionClientSettings.VariableDefaults
                  )
                : "no-defaults"
            }
            solution={solutionData}
            importedJSON={importedJSON}
            setImportedJSON={setImportedJSON}
            manualFields={manualFields}
            setManualFields={setManualFields}
            automaticallySelectedFields={automaticallySelectedFields}
            setAutomaticallySelectedFields={setAutomaticallySelectedFields}
            initialVariableDefaults={
              solutionInfo?.SolutionClientSettings?.VariableDefaults as any
            }
            onChange={setVariableDefaults as any}
            onMonitorsChange={setMonitors}
            onInboundAPIsChange={setInboundAPIs}
            onInboundCampaignsChange={setInboundCampaigns}
            showDefaultValueInputsOnly={!!solutionInfo?.WasDeployed}
          />
        </div>
      )}

      <div className="d-flex justify-content-end mt-1 gap-1 px-3">
        <button
          className="btn btn-dark"
          onClick={() => setShow(false)}
          disabled={isDeploying}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn btn-dark"
          onClick={async () => {
            if (!confirmDialog) return;

            if (
              (await confirmDialog(
                "Reset solution",
                `Are you sure you want to reset this solution?`
              )) === false
            ) {
              return;
            }
            setIsResetting(true);
            const res = await reset({
              uuid: solutionClientUuid,
            });
            setTimeout(() => {
              if ("data" in res) {
                toast.success("Solution reset successfully");
              }
              setIsResetting(false);
            }, 700);
          }}
          disabled={isSaving || isResetting}
        >
          Reset Solution{" "}
          {isResetting ? (
            <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
          ) : (
            ""
          )}
        </button>

        {solutionInfo?.WasDeployed ? (
          <>
            <button
              type="button"
              className="btn btn-colored"
              onClick={async () => {
                setIsSaving(true);
                const res = await save({
                  uuid: solutionClientUuid,
                  body: variableDefaults,
                });
                setTimeout(() => {
                  if ("data" in res) {
                    // alert("Variable defaults saved successfully");
                    toast.success("Variable defaults saved successfully");
                  }
                  setIsSaving(false);
                }, 700);
              }}
              disabled={isSaving || isResetting}
            >
              Save Variable Defaults{" "}
              {isSaving ? (
                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
              ) : (
                ""
              )}
            </button>
          </>
        ) : (
          <button
            className="btn btn-colored"
            onClick={handleDeploy}
            disabled={
              isDeployDisabled ||
              isDeploying ||
              hasMissingInboundChannel ||
              // Disable if any manually managed field is set to create new but has no name
              Object.entries(importedJSON.Bag.External || {}).flatMap(
                ([fieldKey, fieldValue]: [string, any]) =>
                  Object.entries(fieldValue || {}).filter(
                    ([key, value]: [string, any]) =>
                      manualFields.includes(key) &&
                      (hasEmptyNewFieldName(value) || isEmptyOrUnmapped(value))
                  )
              ).length > 0
            }
          >
            Confirm Deploy
          </button>
        )}
      </div>

      <div>
        <JsonCollapse
          json={{
            mapping,
            importedJSON,
            // solution,
            monitors,
            inboundAPIs,
            inboundCampaigns,
            variableDefaults,
            manualFields,
          }}
        />
      </div>
    </div>
  );
}
